import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import { Container, Navbar, Button, Modal } from "react-bootstrap";
import {
  SuiClientProvider,
  WalletProvider,
  useCurrentAccount,
  useCurrentWallet,
  useConnectWallet,
  useWallets,
  useDisconnectWallet,
  useSignPersonalMessage,
} from "@mysten/dapp-kit"; // Updated to use the new dapp-kit
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SuiClient } from "@mysten/sui/client";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
  useNavigate,
  useLocation,
} from "react-router-dom";
import logo from "./img/su-logo.png";
import Dashboard from "./Dashboard";
import Chat from "./Chat";
import Settings from "./Settings";
import { isMobileDevice, parseMobileError } from "./utils/mobileWallet";
import MobileErrorModal from "./components/MobileErrorModal";
import logger from './utils/logger';
import { RiWallet3Line, RiSettings3Line, RiLogoutBoxLine } from 'react-icons/ri';
import { CallProvider, useCall } from './context/CallContext';
import VoiceCallModal from './components/VoiceCallModal';
import IncomingCallModal from './components/IncomingCallModal';

// Auth callback component for handling return from Slush wallet
function AuthCallback() {
  const navigate = useNavigate();
  const currentAccount = useCurrentAccount();
  const { mutate: signPersonalMessage } = useSignPersonalMessage();
  const connect = useConnectWallet();
  const [menuColor] = useState("#ff00ff");
  const [retryCount, setRetryCount] = React.useState(0);
  const [showManualConnect, setShowManualConnect] = React.useState(false); //

  React.useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        logger.log("Handling auth callback from Slush wallet");

        // Check if this is a deep link callback
        const authPending = sessionStorage.getItem("authPending");
        const authTimestamp = sessionStorage.getItem("authTimestamp");

        if (authPending && authTimestamp) {
          // Clear the session storage
          sessionStorage.removeItem("authPending");
          sessionStorage.removeItem("authTimestamp");

          // Check if user is connected
          if (currentAccount) {
            logger.log("Wallet connected, proceeding with signature");

            // Sign authentication message to prove ownership
            const message = `Sign in to SU Messaging\n\nAddress: ${
              currentAccount.address
            }\nTimestamp: ${new Date().toISOString()}`;

            signPersonalMessage(
              {
                message: new TextEncoder().encode(message),
              },
              {
                onSuccess: (result) => {
                  logger.log("Authentication signature successful:", result);
                  navigate("/dashboard");
                },
                onError: (error) => {
                  logger.error("Authentication signature failed:", error);
                  navigate("/");
                },
              }
            );
          } else {
            logger.log("Wallet not connected yet, waiting...");

            // If not connected after delay, show manual connect option
            setTimeout(() => {
              if (!currentAccount) {
                setShowManualConnect(true);
              }
            }, 3000);
          }
        } else {
          // Regular callback handling
          if (currentAccount) {
            navigate("/dashboard");
          } else {
            navigate("/"); //main menu
          }
        }
      } catch (error) {
        logger.error("Auth callback failed:", error);
        navigate("/");
      }
    };

    // Small delay to ensure wallet state is updated
    setTimeout(handleAuthCallback, 1500);
  }, [currentAccount, signPersonalMessage, navigate, retryCount]);

  const handleManualConnect = async () => {
    try {
      setRetryCount((prev) => prev + 1);
      await connect.mutateAsync();
    } catch (error) {
      logger.error("Manual connect failed:", error);
    }
  };

  return (
    <Container
      className="mt-4 text-center"
      style={{
        background: "linear-gradient(135deg, #050010, #150030)",
        border: `4px solid ${menuColor}`,
        borderRadius: "12px",
        boxShadow: "0 0 20px #00ffff",
        color: "#00ffff",
        fontFamily: "Orbitron, sans-serif",
        padding: "20px",
      }}
    >
      <h2 style={{ textShadow: "0 0 15px #00ffff" }}>
        Completing Authentication...
      </h2>
      <p style={{ textShadow: "0 0 6px #ff00ff" }}>
        Please wait while we verify your wallet connection and complete the
        sign-in process.
      </p>
      <div style={{ margin: "20px 0" }}>
        <div
          style={{
            width: "40px",
            height: "40px",
            border: `3px solid ${menuColor}`,
            borderTop: "3px solid transparent",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto",
          }}
        />
      </div>

      {showManualConnect && (
        <div style={{ marginTop: "20px" }}>
          <p style={{ fontSize: "0.9em", opacity: 0.8, marginBottom: "15px" }}>
            If authentication didn't complete automatically, please connect
            manually:
          </p>
          <Button
            onClick={handleManualConnect}
            style={{
              backgroundColor: menuColor,
              borderColor: menuColor,
              textShadow: "0 0 6px #00ffff",
              padding: "8px 16px",
              borderRadius: "8px",
              fontSize: "0.9em",
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#00ffff")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = menuColor)}
          >
            Connect Wallet Manually
          </Button>
        </div>
      )}

      <p style={{ fontSize: "0.9em", opacity: 0.8, marginTop: "15px" }}>
        Make sure your Slush wallet is unlocked and you've approved the
        connection.
      </p>
    </Container>
  );
}

function AppContent() {
  const { activeCall, incomingCall } = useCall();
  const [userName, setUserName] = useState("");
  const [menuColor, setMenuColor] = useState("#ff00ff"); // Default to pink from Chat.js
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showConnectPrompt, setShowConnectPrompt] = useState(false);
  const connect = useConnectWallet({
    onSuccess: () => {
      logger.log("Wallet connected successfully");

      // After successful connection, try to sign authentication message
      if (currentAccount) {
        const message = `Sign in to SU Messaging\n\nAddress: ${
          currentAccount.address
        }\nTimestamp: ${new Date().toISOString()}`;

        signPersonalMessage(
          {
            message: new TextEncoder().encode(message),
          },
          {
            onSuccess: (result) => {
              logger.log("Authentication signature successful:", result);
              navigate("/dashboard");
            },
            onError: (error) => {
              logger.log(
                "Authentication signature failed, but proceeding with login:",
                error
              );
              // Don't show error modal for signature failures - proceed with login
              navigate("/dashboard");
            },
          }
        );
      } else {
        // If no account yet, wait a bit and try again (for mobile redirect timing)
        setTimeout(() => {
          if (currentAccount) {
            const message = `Sign in to SU Messaging\n\nAddress: ${
              currentAccount.address
            }\nTimestamp: ${new Date().toISOString()}`;

            signPersonalMessage(
              {
                message: new TextEncoder().encode(message),
              },
              {
                onSuccess: (result) => {
                  logger.log("Authentication signature successful:", result);
                  navigate("/dashboard");
                },
                onError: (error) => {
                  logger.log(
                    "Authentication signature failed, but proceeding with login:",
                    error
                  );
                  // Don't show error modal for signature failures - proceed with login
                  navigate("/dashboard");
                },
              }
            );
          } else {
            // Only show error if we still don't have an account after timeout
            logger.error("No account found after connection attempt");
            const errorType = parseMobileError(new Error("Connection timeout"));
            setMobileError(new Error("Connection timeout"));
            setMobileErrorType(errorType);
            setShowMobileError(true);
          }
        }, 1000);
      }
    },
    onError: (error) => {
      logger.error("Wallet connection failed:", error);
      const errorType = parseMobileError(error);
      setMobileError(error);
      setMobileErrorType(errorType);
      setShowMobileError(true);
    },
  });
  const disconnect = useDisconnectWallet();
  const wallets = useWallets();
  const currentAccount = useCurrentAccount();
  const currentWallet = useCurrentWallet();
  const isConnected = !!currentAccount;
  const [showWalletDropdown, setShowWalletDropdown] = useState(false);
  const walletDropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Sign personal message for authentication
  const { mutate: signPersonalMessage } = useSignPersonalMessage();

  // Mobile error modal state
  const [showMobileError, setShowMobileError] = useState(false);
  const [mobileError, setMobileError] = useState(null);
  const [mobileErrorType, setMobileErrorType] = useState(null);

  useEffect(() => {
    const client = new SuiClient({
      url: "https://fullnode.mainnet.sui.io:443",
    });
    const fetchUserName = async () => {
      if (isConnected && currentAccount) {
        const objects = await client.getOwnedObjects({
          owner: currentAccount.address,
          options: { showType: true, showContent: true },
        });
        const userObject = objects.data.find((obj) =>
          obj.data.type.includes(
            "0x2aaad5d1ce7482b5850dd11642358bf23cb0e6432b12a581eb77d212dca54045::su_messaging::User"
          )
        );
        setUserName(
          userObject?.data.content.fields.display_name
            ? new TextDecoder().decode(
                new Uint8Array(userObject.data.content.fields.display_name)
              )
            : localStorage.getItem("currentDisplayName") ||
                currentAccount.address.slice(0, 6) + "..."
        );
      }
    };
    fetchUserName();

    // Dynamic color animation
    let r = 255;
    let g = 0;
    let b = 255;
    const interval = setInterval(() => {
      r = (r + 1) % 256;
      g = (g + 2) % 256;
      b = (b + 3) % 256;
      setMenuColor(`rgb(${r}, ${g}, ${b})`);
    }, 100);
    return () => clearInterval(interval);
  }, [isConnected, currentAccount, location.pathname]);

  // Navigate to dashboard when wallet connects (including auto-connect)
  useEffect(() => {
    if (isConnected && currentAccount && location.pathname === "/") {
      navigate("/dashboard");
    }
  }, [isConnected, currentAccount, navigate, location.pathname]);

  // Close wallet dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showWalletDropdown &&
        walletDropdownRef.current &&
        !walletDropdownRef.current.contains(event.target)
      ) {
        setShowWalletDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showWalletDropdown]);

  const handleDashboardClick = (e) => {
    if (!isConnected) {
      e.preventDefault();
      setShowConnectPrompt(true);
    }
  };

  const handleWebConnect = () => {
    // Connect to wallet - dapp-kit will handle Slush wallet detection and mobile redirects
    // Error handling is done in the useConnectWallet hook callbacks
    connect.mutate();
  };

  // Enhanced mobile detection
  const isMobile = isMobileDevice();

  const handleConnectWallet = () => {
    const slushWallet = wallets.find((wallet) =>
      wallet.name.toLowerCase().includes("slush")
    );
    if (slushWallet) {
      logger.log("Connecting to Slush Wallet");
      connect.mutate({ wallet: slushWallet });
    } else {
      logger.log("Slush Wallet not found, using default connect");
      connect.mutate();
    }
  };

  return (
    <div>
      <Navbar
        bg="dark"
        variant="dark"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          borderBottom: `2px solid ${menuColor}`,
          borderRadius: 0,
          boxShadow: `0 2px 16px rgba(0,0,0,0.5), 0 0 12px ${menuColor}25`,
          fontFamily: "Orbitron, sans-serif",
          color: "#00ffff",
          background: "linear-gradient(180deg, #050010 0%, #0d001f 100%)",
          padding: 0,
          minHeight: "56px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", width: "100%", padding: "0 12px", minHeight: "56px" }}>
          {/* Brand - left, truncates if username is long */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: "0 1 auto", minWidth: 0, overflow: "hidden" }}>
            <Link to="/">
              <img
                src={logo}
                alt="SU Logo"
                style={{
                  width: "36px",
                  height: "36px",
                  border: `2px solid ${menuColor}`,
                  borderRadius: "8px",
                  flexShrink: 0,
                  display: "block",
                }}
              />
            </Link>
            <Link to="/" className="text-white text-decoration-none" style={{ minWidth: 0 }}>
              <span style={{
                textShadow: "0 0 12px #00ffff",
                fontSize: "1.1em",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
                maxWidth: "min(140px, 28vw)",
              }}>
                {userName || "SU"}
              </span>
            </Link>
          </div>

          {/* Flex spacer */}
          <div style={{ flex: 1 }} />

          {/* Wallet — always pinned to right */}
          {isConnected ? (
            <div ref={walletDropdownRef} style={{ position: "relative", flexShrink: 0 }}>
                {/* Trigger button — shows truncated address */}
                <button
                  onClick={() => setShowWalletDropdown(!showWalletDropdown)}
                  style={{
                    background: `linear-gradient(135deg, ${menuColor}20, ${menuColor}40)`,
                    border: `1.5px solid ${menuColor}`,
                    borderRadius: "8px",
                    color: "#00ffff",
                    fontFamily: "Orbitron, sans-serif",
                    fontSize: "11px",
                    padding: "6px 10px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    minHeight: "34px",
                    boxShadow: `0 0 10px ${menuColor}40`,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = `0 0 15px ${menuColor}60`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = `0 0 10px ${menuColor}40`;
                  }}
                >
                  <RiWallet3Line size={14} />
                  {currentAccount.address.slice(0, 6)}...{currentAccount.address.slice(-4)}
                  <span style={{ fontSize: "8px", opacity: 0.7 }}>▾</span>
                </button>

                {/* Dropdown panel */}
                {showWalletDropdown && (
                  <div style={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    right: 0,
                    minWidth: "180px",
                    background: "linear-gradient(135deg, #050010, #0d001f)",
                    border: `2px solid ${menuColor}`,
                    borderRadius: "10px",
                    boxShadow: `0 0 20px ${menuColor}60`,
                    zIndex: 9999,
                    padding: "8px 0",
                    fontFamily: "Orbitron, sans-serif",
                  }}>
                    {/* Wallet name */}
                    <div style={{ padding: "6px 14px", fontSize: "10px", color: "#00ffff", borderBottom: `1px dashed ${menuColor}`, marginBottom: "4px" }}>
                      <div style={{ opacity: 0.7 }}>{currentWallet?.name || "Connected"}</div>
                      <div style={{ fontFamily: "monospace", fontSize: "9px", marginTop: "2px" }}>
                        {currentAccount.address.slice(0, 10)}...{currentAccount.address.slice(-6)}
                      </div>
                    </div>

                    {/* Settings */}
                    <Link to="/settings"
                      onClick={() => setShowWalletDropdown(false)}
                      style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 14px", color: "#00ffff", textDecoration: "none", fontSize: "12px", cursor: "pointer" }}
                    >
                      <RiSettings3Line size={14} /> Settings
                    </Link>

                    {/* Logout */}
                    <button
                      onClick={() => { disconnect.mutate(); setShowWalletDropdown(false); }}
                      style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%", padding: "8px 14px", background: "none", border: "none", color: "#ff4444", fontFamily: "Orbitron, sans-serif", fontSize: "12px", cursor: "pointer", justifyContent: "flex-start" }}
                    >
                      <RiLogoutBoxLine size={14} /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // Disconnected State — Connect Button
              <button onClick={handleConnectWallet} style={{ background: menuColor, border: "none", borderRadius: "8px", color: "#050010", fontFamily: "Orbitron, sans-serif", fontSize: "12px", padding: "8px 14px", cursor: "pointer", fontWeight: "bold", minHeight: "34px", display: "flex", alignItems: "center", gap: "6px", transition: "all 0.2s ease" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 0 15px ${menuColor}60`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                }}>
                <RiWallet3Line size={14} /> Connect
              </button>
            )}
        </div>
      </Navbar>
      <Routes>
        <Route
          path="/"
          element={
            isConnected ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Container
                className="mt-4 text-center"
                style={{
                  background: "linear-gradient(135deg, #050010, #150030)",
                  border: `4px solid ${menuColor}`,
                  borderRadius: "12px",
                  boxShadow: "0 0 20px #00ffff",
                  color: "#00ffff",
                  fontFamily: "Orbitron, sans-serif",
                  padding: "20px",
                }}
              >
                <h1
                  className="d-flex align-items-center justify-content-center gap-2"
                  style={{
                    textShadow: "0 0 15px #00ffff",
                    fontSize: "2em",
                  }}
                >
                  Welcome to {userName || "SU"}
                  <img
                    src={logo}
                    alt="SU Logo"
                    style={{
                      width: "50px",
                      height: "50px",
                      border: `2px solid ${menuColor}`,
                      borderRadius: "8px",
                    }}
                  />
                </h1>
                <p
                  style={{
                    textShadow: "0 0 6px #ff00ff",
                    fontSize: "1.1em",
                  }}
                >
                  A decentralized messaging app powered by the SUI blockchain.
                </p>
                <div className="mt-4">
                  <Button
                    as={Link}
                    to="/dashboard"
                    variant="outline-primary"
                    className="mx-2"
                    style={{
                      background: `linear-gradient(135deg, ${menuColor}20, ${menuColor}40)`,
                      border: `2px solid ${menuColor}`,
                      color: "#00ffff",
                      textShadow: "0 0 8px #00ffff",
                      padding: "12px 24px",
                      fontSize: "1.1em",
                      borderRadius: "12px",
                      fontWeight: "bold",
                      boxShadow: `0 0 15px ${menuColor}40`,
                      transition: "all 0.3s ease",
                      fontFamily: '"Orbitron", sans-serif',
                      minHeight: "48px",
                    }}
                    onClick={handleDashboardClick}
                    onMouseEnter={(e) => {
                      e.target.style.boxShadow = `0 0 25px ${menuColor}60`;
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.background = `linear-gradient(135deg, ${menuColor}30, ${menuColor}50)`;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.boxShadow = `0 0 15px ${menuColor}40`;
                      e.target.style.transform = "translateY(0)";
                      e.target.style.background = `linear-gradient(135deg, ${menuColor}20, ${menuColor}40)`;
                    }}
                  >
                    🚀 Go to Dashboard
                  </Button>
                </div>
              </Container>
            )
          }
        />
        <Route
          path="/dashboard"
          element={isConnected ? <Dashboard /> : <Navigate to="/" replace />}
        />
        <Route path="/chat/:id" element={<Chat />} />
        <Route
          path="/settings"
          element={<Settings setMenuColor={setMenuColor} />}
        />
        <Route path="/auth-callback" element={<AuthCallback />} />
      </Routes>

      <Modal
        show={showWalletModal}
        onHide={() => setShowWalletModal(false)}
        centered
        size="md"
        style={{
          background: "rgba(0, 0, 0, 0.9)",
          backdropFilter: "blur(10px)",
          maxWidth: "90%",
        }}
      >
        <Modal.Header
          style={{
            background: "linear-gradient(135deg, #050010, #150030)",
            borderBottom: `2px solid ${menuColor}`,
            color: "#00ffff",
            fontFamily: "Orbitron, sans-serif",
          }}
        >
          <Modal.Title style={{ textShadow: "0 0 12px #00ffff" }}>
            🔗 Connect Wallet
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            background: "#050010",
            color: "#00ffff",
            textShadow: "0 0 4px #ff00ff",
            fontSize: "1em",
            padding: "25px",
          }}
        >
          {isMobile ? (
            <div>
              <div
                style={{
                  textAlign: "center",
                  marginBottom: "25px",
                }}
              >
                <div
                  style={{
                    fontSize: "3em",
                    marginBottom: "10px",
                    textShadow: "0 0 20px #00ffff",
                    animation: "pulse 2s infinite",
                  }}
                >
                  �
                </div>
                <h4
                  style={{
                    color: "#00ffff",
                    textShadow: "0 0 12px #00ffff",
                    marginBottom: "15px",
                    fontFamily: "Orbitron, sans-serif",
                  }}
                >
                  Connect with Slush Wallet
                </h4>
                <p
                  style={{
                    color: "#ffffff",
                    marginBottom: "20px",
                    fontSize: "1em",
                  }}
                >
                  Sign in securely with your Slush Wallet to access SU Messaging
                </p>
              </div>

              {/* Cyberpunk-styled Slush Wallet Button */}
              <div
                onClick={handleWebConnect}
                style={{
                  background: `linear-gradient(135deg, ${menuColor}20, ${menuColor}40)`,
                  border: `3px solid ${menuColor}`,
                  borderRadius: "16px",
                  padding: "20px",
                  marginBottom: "20px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  fontFamily: "Orbitron, sans-serif",
                  boxShadow: `0 0 25px ${menuColor}40, inset 0 0 25px ${menuColor}10`,
                  position: "relative",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  e.target.style.boxShadow = `0 0 35px ${menuColor}60, inset 0 0 35px ${menuColor}20`;
                  e.target.style.transform = "translateY(-3px)";
                  e.target.style.borderColor = "#00ffff";
                }}
                onMouseLeave={(e) => {
                  e.target.style.boxShadow = `0 0 25px ${menuColor}40, inset 0 0 25px ${menuColor}10`;
                  e.target.style.transform = "translateY(0)";
                  e.target.style.borderColor = menuColor;
                }}
              >
                {/* Shimmer effect */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(45deg, transparent 30%, ${menuColor}30 50%, transparent 70%)`,
                    animation: "shimmer 3s infinite",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                />

                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    backgroundColor: "#00ff00",
                    boxShadow: "0 0 12px #00ff00",
                    animation: "pulse 2s infinite",
                    zIndex: 2,
                    position: "relative",
                  }}
                />

                <div
                  style={{
                    flex: 1,
                    zIndex: 2,
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      color: "#00ffff",
                      fontSize: "1.2em",
                      fontWeight: "bold",
                      textShadow: "0 0 8px #00ffff",
                      marginBottom: "4px",
                    }}
                  >
                    Slush Wallet
                  </div>
                  <div
                    style={{
                      color: "#ffffff",
                      fontSize: "0.9em",
                      opacity: 0.9,
                    }}
                  >
                    Secure blockchain authentication
                  </div>
                </div>

                <div
                  style={{
                    color: menuColor,
                    fontSize: "1.5em",
                    textShadow: `0 0 8px ${menuColor}`,
                    zIndex: 2,
                    position: "relative",
                  }}
                >
                  →
                </div>
              </div>

              <div
                style={{
                  background: "rgba(0, 255, 255, 0.1)",
                  border: "2px solid #00ffff",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "15px",
                  fontSize: "0.9em",
                }}
              >
                <div
                  style={{
                    color: "#00ffff",
                    fontWeight: "bold",
                    marginBottom: "8px",
                    textShadow: "0 0 6px #00ffff",
                  }}
                >
                  🔐 Secure Sign-In Process:
                </div>
                <div style={{ color: "#ffffff", lineHeight: "1.5" }}>
                  1. Tap "Connect with Slush Wallet"
                  <br />
                  2. Get redirected to Slush wallet app
                  <br />
                  3. Review and sign authentication message
                  <br />
                  4. Return to SU Messaging automatically
                </div>
              </div>

              <div
                style={{
                  textAlign: "center",
                  color: menuColor,
                  fontSize: "0.8em",
                  opacity: 0.8,
                  textShadow: `0 0 4px ${menuColor}`,
                }}
              >
                Your connection is encrypted and secure
              </div>
            </div>
          ) : (
            <div>
              <p style={{ marginBottom: "20px", fontSize: "1.1em" }}>
                Connect your Slush Wallet to start using SU Messaging:
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {/* Slush Wallet Option Only */}
                <div
                  onClick={() => {
                    logger.log("Connecting to Slush Wallet");
                    handleWebConnect();
                    setShowWalletModal(false);
                  }}
                  style={{
                    padding: "20px",
                    cursor: "pointer",
                    background: `linear-gradient(135deg, ${menuColor}10, ${menuColor}20)`,
                    border: `3px solid ${menuColor}40`,
                    borderRadius: "16px",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    fontFamily: "Orbitron, sans-serif",
                    fontSize: "1.2em",
                    fontWeight: "500",
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = `linear-gradient(135deg, ${menuColor}20, ${menuColor}30)`;
                    e.target.style.borderColor = menuColor;
                    e.target.style.transform = "translateY(-3px)";
                    e.target.style.boxShadow = `0 0 25px ${menuColor}40`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = `linear-gradient(135deg, ${menuColor}10, ${menuColor}20)`;
                    e.target.style.borderColor = `${menuColor}40`;
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  {/* Shimmer effect */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(45deg, transparent 30%, ${menuColor}30 50%, transparent 70%)`,
                      animation: "shimmer 3s infinite",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  />

                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "50%",
                      backgroundColor: "#00ff00",
                      boxShadow: "0 0 12px #00ff00",
                      animation: "pulse 2s infinite",
                      zIndex: 2,
                      position: "relative",
                    }}
                  />

                  <div
                    style={{
                      flex: 1,
                      zIndex: 2,
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        color: "#00ffff",
                        fontSize: "1.3em",
                        fontWeight: "bold",
                        textShadow: "0 0 8px #00ffff",
                        marginBottom: "4px",
                      }}
                    >
                      Slush Wallet
                    </div>
                    <div
                      style={{
                        color: "#ffffff",
                        fontSize: "0.9em",
                        opacity: 0.9,
                      }}
                    >
                      Secure blockchain authentication
                    </div>
                  </div>

                  <div
                    style={{
                      color: menuColor,
                      fontSize: "1.5em",
                      textShadow: `0 0 8px ${menuColor}`,
                      zIndex: 2,
                      position: "relative",
                    }}
                  >
                    →
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: "20px",
                  padding: "16px",
                  background: "rgba(0, 255, 255, 0.1)",
                  border: "2px solid #00ffff",
                  borderRadius: "12px",
                  fontSize: "0.9em",
                }}
              >
                <div
                  style={{
                    color: "#00ffff",
                    fontWeight: "bold",
                    marginBottom: "8px",
                    textShadow: "0 0 6px #00ffff",
                  }}
                >
                  🔐 Secure Sign-In Process:
                </div>
                <div style={{ color: "#ffffff", lineHeight: "1.5" }}>
                  1. Tap "Slush Wallet" to connect
                  <br />
                  2. Get redirected to Slush wallet app
                  <br />
                  3. Review and sign authentication message
                  <br />
                  4. Return to SU Messaging automatically
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{
            background: "linear-gradient(135deg, #050010, #150030)",
            borderTop: `2px solid ${menuColor}`,
            justifyContent: "center",
            padding: "15px",
          }}
        >
          <Button
            variant="secondary"
            onClick={() => setShowWalletModal(false)}
            style={{
              backgroundColor: "transparent",
              border: `2px solid ${menuColor}`,
              color: menuColor,
              textShadow: "0 0 6px #00ffff",
              padding: "10px 20px",
              fontSize: "1em",
              borderRadius: "12px",
              transition: "all 0.3s",
              fontFamily: "Orbitron, sans-serif",
              fontWeight: "bold",
              minWidth: "120px",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = `${menuColor}20`;
              e.target.style.color = "#00ffff";
              e.target.style.boxShadow = `0 0 15px ${menuColor}40`;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "transparent";
              e.target.style.color = menuColor;
              e.target.style.boxShadow = "none";
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Mobile Error Modal */}
      <MobileErrorModal
        show={showMobileError}
        onHide={() => setShowMobileError(false)}
        error={mobileError}
        errorType={mobileErrorType}
        onRetry={handleWebConnect}
        menuColor={menuColor}
      />

      {/* Voice call modals */}
      {incomingCall && !activeCall && <IncomingCallModal />}
      {activeCall && <VoiceCallModal />}

      {/* Connect Wallet Prompt Popup */}
      <Modal
        show={showConnectPrompt}
        onHide={() => setShowConnectPrompt(false)}
        centered
        size="sm"
        style={{
          background: "rgba(0, 0, 0, 0.9)",
          backdropFilter: "blur(15px)",
        }}
      >
        <Modal.Body
          style={{
            background: "linear-gradient(135deg, #050010, #150030)",
            border: `3px solid ${menuColor}`,
            borderRadius: "20px",
            color: "#00ffff",
            textAlign: "center",
            padding: "30px 20px",
            fontFamily: "Orbitron, sans-serif",
            boxShadow: `0 0 40px ${menuColor}80, inset 0 0 40px ${menuColor}20`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Animated background effect */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(45deg, transparent 20%, ${menuColor}15 50%, transparent 80%)`,
              animation: "shimmer 2s infinite",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />

          <div style={{ position: "relative", zIndex: 2 }}>
            <div
              style={{
                fontSize: "3em",
                marginBottom: "15px",
                textShadow: "0 0 20px #00ffff",
                animation: "pulse 2s infinite",
              }}
            >
              🔗
            </div>

            <h4
              style={{
                color: "#00ffff",
                textShadow: "0 0 12px #00ffff",
                marginBottom: "15px",
                fontSize: "1.4em",
                fontWeight: "bold",
              }}
            >
              Connect Your Wallet
            </h4>

            <p
              style={{
                color: "#ffffff",
                marginBottom: "25px",
                fontSize: "1em",
                opacity: 0.9,
              }}
            >
              To access the dashboard and start messaging, please connect your
              Slush Wallet.
            </p>

            <Button
              onClick={() => {
                setShowConnectPrompt(false);
                const slushWallet = wallets.find((wallet) =>
                  wallet.name.toLowerCase().includes("slush")
                );
                if (slushWallet) {
                  logger.log("Connecting to Slush Wallet");
                  connect.mutate({ wallet: slushWallet });
                } else {
                  logger.log("Slush Wallet not found, using default connect");
                  connect.mutate();
                }
              }}
              style={{
                background: `linear-gradient(135deg, ${menuColor}, ${menuColor}dd)`,
                border: `2px solid ${menuColor}`,
                color: "#00ffff",
                textShadow: "0 0 8px #00ffff",
                fontSize: "16px",
                fontWeight: "bold",
                padding: "12px 30px",
                borderRadius: "12px",
                fontFamily: "Orbitron, sans-serif",
                boxShadow: `0 0 20px ${menuColor}60`,
                transition: "all 0.3s ease",
                minWidth: "160px",
              }}
              onMouseEnter={(e) => {
                e.target.style.boxShadow = `0 0 30px ${menuColor}80`;
                e.target.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.target.style.boxShadow = `0 0 20px ${menuColor}60`;
                e.target.style.transform = "translateY(0)";
              }}
            >
              <span style={{ fontSize: "18px", marginRight: "8px" }}>🔗</span>
              Connect Wallet
            </Button>

            <div
              style={{
                marginTop: "15px",
                fontSize: "0.8em",
                opacity: 0.7,
                color: "#ffffff",
              }}
            >
              Secure • Encrypted • Decentralized
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

function App() {
  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <SuiClientProvider
        networks={{
          mainnet: { url: "https://fullnode.mainnet.sui.io:443" },
          testnet: { url: "https://fullnode.testnet.sui.io:443" },
        }}
        defaultNetwork="mainnet"
      >
        <WalletProvider autoConnect={true}>
          <Router>
            <CallProvider>
              <AppContent />
            </CallProvider>
          </Router>
        </WalletProvider>
      </SuiClientProvider>
    </QueryClientProvider>
  );
}

export default App;
